// ── RiskAcceptancesIndexPage — RA Operations Workspace (RA-BI P1) ────────────
//
// Route: /risk-acceptances. P1 adds, above the existing renewal-chain list: a 6-KPI operations
// row, an 8-state lifecycle strip, composable URL-backed filter chips in the search field, a
// net-new search (Enter/Search-click), and the "Requires my action" lens. ⚠ Operational + vendor-
// scoped, deliberately distinct from the Enterprise Risk Dashboard (no category/enterprise trend).
// Honesty: no blended risk score; residual-NULL is a completeness reading (never low); expiry from
// the stored date; median latency completed-only; sparse tenants render honest gaps.

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight, ShieldAlert, Search, X } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import type { TooltipProps } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { InfoTooltip } from '@/components/shared/InfoTooltip';
import { KpiCard } from '@/components/shared/KpiCard';
import { useAuthStore } from '@/store/auth.store';
import { useRiskAcceptances, useRiskAcceptanceAction, useRaOverview } from '@/api/hooks/useRiskAcceptances';
import { useVendors } from '@/api/hooks/useVendors';
import { RiskAcceptanceActionModal } from '@/components/shared/RiskAcceptanceActionModal';
import { VendorRenewalChainGroup, VendorOrphanChildRow } from '@/components/risk-acceptances/VendorRenewalChainGroup';
import { cn } from '@/lib/utils';
import type { RaEntry, RaOverview } from '@/api/types/risk-acceptance.types';
import { RR_ACTIVE_STATES, RR_RESOLVED_STATES } from '@/components/risk-acceptances/rrPresentation';
import type { VendorRaActionType } from '@/components/risk-acceptances/VendorRenewalChainGroup';

// ── Renewal-chain grouping (unchanged) ────────────────────────────────────────
function groupRaItems(items: RaEntry[]): { groups: { parent: RaEntry; children: RaEntry[] }[]; orphans: RaEntry[] } {
  const idSet = new Set(items.map((e) => e.id));
  const parentMap = new Map<string, RaEntry[]>();
  for (const item of items) {
    if (item.parent_acceptance_id !== null && idSet.has(item.parent_acceptance_id)) {
      const arr = parentMap.get(item.parent_acceptance_id) ?? [];
      arr.push(item); parentMap.set(item.parent_acceptance_id, arr);
    }
  }
  const childIds = new Set<string>();
  for (const children of parentMap.values()) for (const c of children) childIds.add(c.id);
  const groups: { parent: RaEntry; children: RaEntry[] }[] = [];
  const orphans: RaEntry[] = [];
  for (const item of items) {
    if (childIds.has(item.id)) continue;
    if (item.parent_acceptance_id !== null && !idSet.has(item.parent_acceptance_id)) orphans.push(item);
    else groups.push({ parent: item, children: parentMap.get(item.id) ?? [] });
  }
  return { groups, orphans };
}

// ── Lifecycle strip — the 7 native workflow_status values grouped by who holds it (SS2B-2a). The
// `closed` holder additionally shows its closure_reason breakdown (display-only sub-counts). ──
const HOLDERS: { label: string; states: { s: string; label: string }[] }[] = [
  { label: 'With submitter', states: [{ s: 'draft', label: 'Draft' }, { s: 'returned', label: 'Returned' }] },
  { label: 'Awaiting approval', states: [{ s: 'pending_approval', label: 'Pending approval' }] },
  { label: 'Active', states: [{ s: 'approved', label: 'Approved' }] },
  { label: 'Closed', states: [{ s: 'denied', label: 'Denied' }, { s: 'withdrawn', label: 'Withdrawn' }, { s: 'closed', label: 'Closed' }] },
];
const STATUS_LABEL: Record<string, string> = Object.fromEntries(HOLDERS.flatMap((h) => h.states.map((st) => [st.s, st.label])));
// The closed holder's closure_reason breakdown (display-only) — sourced from closure_reason_counts.
const CLOSURE_LABEL: { r: string; label: string }[] = [
  { r: 'remediated', label: 'Remediated' }, { r: 'expired', label: 'Expired' }, { r: 'revoked', label: 'Revoked' },
  { r: 'replaced_by_renewal', label: 'Replaced by renewal' }, { r: 'app_service_retired', label: 'App/service retired' },
  { r: 'no_longer_applicable', label: 'No longer applicable' }, { r: 'other', label: 'Other' },
];

// ── Page-size selector (item 5) — 25/50/100/All, default 25. ⚠ "All" maps to the backend cap
// (limit.max(100)); true-unbounded is deferred (rides B-VENDORS-LIST-VIRTUALIZATION-DEFERRED), so
// "All" shows up to 100 with an honest note. The CHOICE is tracked separately from the numeric limit
// so "100" and "All" (both → 100) stay visually distinct.
const PAGE_SIZES: (number | 'All')[] = [25, 50, 100, 'All'];
const MAX_PAGE_SIZE = 100;

function PageSizeSelect({ value, onChange }: { value: number | 'All'; onChange: (n: number | 'All') => void }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-text-secondary">Show</span>
      {PAGE_SIZES.map((n) => (
        <button key={String(n)} type="button" onClick={() => onChange(n)}
          title={n === 'All' ? `Up to ${MAX_PAGE_SIZE} (server cap; full-list view coming)` : undefined}
          className={cn('text-[11px] font-semibold rounded px-1.5 py-0.5 transition-colors',
            value === n ? 'bg-accent-cyan/15 text-accent-cyan' : 'text-text-secondary hover:text-text-primary')}>
          {n}
        </button>
      ))}
    </div>
  );
}

// ── KPI config — copy VERIFIED against getRaOverview (honesty floor on help text) ──
type KpiRender = { key: string; label: string; value: string; sub?: string; info: string; onClick?: () => void };

// ── P2 charts ─────────────────────────────────────────────────────────────────
// Token hexes (Trap #55 — recharts needs color strings; mirror tailwind.config.ts).
const C = { valid: '#3FB980', warn: '#D7A94A', high: '#E07B4F', expired: '#C2453F', cyan: '#009FFD', slate: '#8298B0', axis: '#5A6A82', grid: '#1E2E42' };

function ChartPanel({ title, info, subtitle, children }: { title: string; info: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-bg-surface border border-border-subtle rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">{title}<InfoTooltip text={info} /></span>
        {subtitle && <span className="text-[11px] text-text-secondary">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}
function ChartEmpty({ note }: { note: string }) {
  return (
    <div className="h-[170px] flex flex-col items-center justify-center gap-1.5 text-center px-4 border border-dashed border-border-subtle rounded-lg">
      <span className="text-[11px] uppercase tracking-wide text-text-faint">Limited data</span>
      <span className="text-[11.5px] text-text-faint leading-relaxed max-w-[38ch]">{note}</span>
    </div>
  );
}

// Chart 1 — Time-to-expiry runway (approved). 0-count buckets draw no bar (honest gap), never fabricated.
function ExpiryRunway({ r, activeTotal }: { r: RaOverview['expiry_runway']; activeTotal: number }) {
  if (activeTotal === 0) return <ChartEmpty note="No active acceptances yet — the runway fills in as acceptances are approved." />;
  const rows = [
    { x: 'Lapsed', n: r.past, fill: C.expired },
    { x: '≤30d', n: r.d0_30, fill: C.high },
    { x: '31–90d', n: r.d31_90, fill: C.warn },
    { x: '91–180d', n: r.d91_180, fill: C.cyan },
    { x: '>180d', n: r.d180_plus, fill: C.valid },
  ];
  return (
    <ResponsiveContainer width="100%" height={170}>
      <BarChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: -20 }}>
        <CartesianGrid stroke={C.grid} vertical={false} />
        <XAxis dataKey="x" tick={{ fill: C.axis, fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: C.axis, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
        <Tooltip cursor={{ fill: 'rgba(255,255,255,0.03)' }} contentStyle={{ background: '#0E1A2B', border: `1px solid ${C.grid}`, borderRadius: 8, fontSize: 12 }} />
        <Bar dataKey="n" name="acceptances" radius={[2, 2, 0, 0]} maxBarSize={40}>
          {rows.map((row) => <Cell key={row.x} fill={row.fill} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ⚠ Tooltip omits zero series so a severity with no residual never reads a spurious "0".
function SevTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const nz = payload.filter((p) => Number(p.value ?? 0) > 0);
  if (!nz.length) return null;
  return (
    <div style={{ background: '#0E1A2B', border: `1px solid ${C.grid}`, borderRadius: 8, fontSize: 12, padding: '6px 10px' }}>
      <div style={{ color: '#EAF2FB', marginBottom: 2 }}>{label}</div>
      {nz.map((p) => <div key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</div>)}
    </div>
  );
}
// Chart 2 — Severity inherent vs residual: TWO INDEPENDENT series; residual-NULL is its own Unspecified.
function SeverityChart({ s, activeTotal }: { s: RaOverview['severity']; activeTotal: number }) {
  if (activeTotal === 0) return <ChartEmpty note="No active acceptances yet — inherent and residual ratings appear once acceptances are approved." />;
  const cats = ['low', 'medium', 'high', 'critical', 'unspecified'] as const;
  const rows = cats.map((c) => ({
    x: c === 'unspecified' ? 'Unspecified' : c[0].toUpperCase() + c.slice(1),
    inherent: c === 'unspecified' ? 0 : (s.inherent as Record<string, number>)[c],
    residual: (s.residual as Record<string, number>)[c] ?? 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={170}>
      <BarChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: -20 }}>
        <CartesianGrid stroke={C.grid} vertical={false} />
        <XAxis dataKey="x" tick={{ fill: C.axis, fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: C.axis, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
        <Tooltip content={<SevTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
        <Bar dataKey="inherent" name="inherent" fill={C.high} radius={[2, 2, 0, 0]} maxBarSize={22} />
        <Bar dataKey="residual" name="residual" fill={C.cyan} radius={[2, 2, 0, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── P3-A approval funnel / latency ──────────────────────────────────────────────
// Three real decision OUTCOMES (approved / returned / denied), each with count, share of the decided base,
// and median latency. ⚠ The decided base = approved + denied + returned ONLY; the pending queue is stated
// SEPARATELY below (never folded into a rate — the gap-not-zero honesty). Empty when nothing has been decided.
const FUNNEL_META = [
  { key: 'approved' as const, label: 'Approved', latency: 'approved_latency_days' as const, fill: C.valid },
  { key: 'returned' as const, label: 'Returned', latency: 'returned_latency_days' as const, fill: C.warn },
  { key: 'denied' as const, label: 'Denied', latency: 'denied_latency_days' as const, fill: C.expired },
];
function ApprovalFunnel({ f }: { f: RaOverview['funnel'] }) {
  const decided = f.approved + f.denied + f.returned;
  if (decided === 0 && f.in_queue === 0) {
    return <ChartEmpty note="No submissions have been decided yet — approval, return and denial rates appear once acceptances move through review." />;
  }
  const pct = (n: number) => (decided === 0 ? '—' : `${Math.round((n / decided) * 100)}%`);
  const lat = (n: number | null) => (n == null ? '—' : `${n}d`);
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-3">
        {FUNNEL_META.map((m) => (
          <div key={m.key} className="rounded-lg border border-border-subtle bg-bg-base/40 p-3 flex flex-col gap-1.5">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ background: m.fill }} />{m.label}
            </span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold text-text-primary tabular-nums">{f[m.key]}</span>
              <span className="text-xs text-text-secondary">· {pct(f[m.key])} of decided</span>
            </div>
            <span className="text-[11px] text-text-secondary pt-1 border-t border-border-subtle/60">
              {f[m.latency] == null ? 'no completed timings yet' : `${lat(f[m.latency])} median to decision`}
            </span>
          </div>
        ))}
      </div>
      {/* ⚠ In-queue is stated separately — NOT counted in the rates above (it is not an outcome). */}
      <div className="flex items-center gap-2 text-[11.5px] text-text-secondary border-t border-border-subtle/60 pt-2.5">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-accent-cyan shrink-0" />
          <span className="font-semibold text-text-primary tabular-nums">{f.in_queue}</span> still awaiting a decision
        </span>
        <span className="text-text-faint">— in the queue now, not counted in the rates above.</span>
      </div>
    </div>
  );
}

// ── P3-B action-required union — a VIEW of the same register records; rows NAVIGATE (never mutate filters). ──
const ATTN_RAIL: Record<number, string> = { 1: 'bg-accent-rose', 2: 'bg-accent-amber', 3: 'bg-accent-cyan', 4: 'bg-text-muted' };
const ATTN_BADGE: Record<number, string> = {
  1: 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border',
  2: 'bg-cert-warn-bg text-cert-warn-fg border-cert-warn-border',
  3: 'bg-accent-cyan/10 text-accent-cyan border-accent-cyan/30',
  4: 'bg-bg-surface-2 text-text-muted border-border-subtle',
};
function AttentionRow({ row, vendorName, onNavigate }: { row: RaOverview['attention'][number]; vendorName: (id: string) => string; onNavigate: (id: string) => void }) {
  const vendor = row.vendor_name ?? (row.vendor_id ? vendorName(row.vendor_id) : null);
  return (
    <button type="button" onClick={() => onNavigate(row.id)}
      className="flex items-center justify-between gap-3 py-2.5 -mx-4 px-4 text-left w-full hover:bg-bg-surface-2/60 transition-colors">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className={cn('w-0.5 h-8 rounded-full shrink-0', ATTN_RAIL[row.severity] ?? 'bg-text-muted')} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">{row.title ?? '—'}{vendor && <span className="text-text-secondary font-normal"> · {vendor}</span>}</p>
          <p className="text-[11.5px] text-text-secondary truncate">{row.reason}</p>
        </div>
      </div>
      <span className={cn('text-[10px] font-bold rounded-md border px-1.5 py-0.5 shrink-0', ATTN_BADGE[row.severity] ?? ATTN_BADGE[4])}>{row.label}</span>
    </button>
  );
}

// ── KPI card — RR-BI-1 KPI-AFFORDANCE: the tile is the shared KpiCard (drill-through affordance keyed on the
// real onClick, behavior-frozen). Same treatment as the Org page (fix once, both benefit). ──

// ── Component ─────────────────────────────────────────────────────────────────
export function RiskAcceptancesIndexPage() {
  useEffect(() => { document.title = 'Vendor Risk Register — VeriGRC'; }, []);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, hasAnyRole } = useAuthStore();

  const canCreate = hasAnyRole(['risk_manager', 'super_admin']);
  const isApproverClass = hasAnyRole(['risk_manager', 'super_admin']);
  const currentUserId = user?.id ?? null;

  const [offset, setOffset] = useState(0);
  // item 5 — page-size choice (25/50/100/All); "All" resolves to the backend cap (MAX_PAGE_SIZE).
  const [pageChoice, setPageChoice] = useState<number | 'All'>(25);
  const pageSize = pageChoice === 'All' ? MAX_PAGE_SIZE : pageChoice;
  // ⚠ Search commits on Enter / Search-click (local state), not per keystroke (the /assessments fix).
  const [searchInput, setSearchInput] = useState(searchParams.get('search') ?? '');
  const [actionModal, setActionModal] = useState<{ action: VendorRaActionType; entry: RaEntry } | null>(null);
  const [attnShowAll, setAttnShowAll] = useState(false); // collapse the attention panel to ~10 rows

  // URL = the source of truth for filters (composable, shareable — reuses the dashboard drill-in params).
  const setFilter = useCallback((key: string, value: string | null) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value == null || value === '') p.delete(key); else p.set(key, value);
      return p;
    });
    setOffset(0);
  }, [setSearchParams]);

  // ⚠ SS2B-2a: the strip filter is now native workflow_status (multi-value; composable within the facet).
  const statusSet = new Set((searchParams.get('workflow_status') ?? '').split(',').filter(Boolean));
  const toggleStatus = useCallback((s: string) => {
    const next = new Set(statusSet);
    if (next.has(s)) next.delete(s); else next.add(s);
    setFilter('workflow_status', next.size ? [...next].join(',') : null);
  }, [statusSet, setFilter]);

  const submitSearch = useCallback(() => setFilter('search', searchInput.trim() || null), [setFilter, searchInput]);
  const clearAll = useCallback(() => { setSearchParams(new URLSearchParams()); setSearchInput(''); setOffset(0); }, [setSearchParams]);

  const requiresAction = searchParams.get('requires_action_by_user') === 'true';
  const searchTerm = searchParams.get('search') ?? '';

  // ⚠ SS2B-2b Phase 3 — Active/Resolved tabs on ONE register (spec card 8; the tab lives in the URL).
  // The tab partitions by workflow_status; an explicit strip selection (workflow_status) refines within
  // it. ⚠ Honesty anchor 1: `approved` is ACTIVE, so an implemented Mitigate is never filed under Resolved.
  const tab: 'active' | 'resolved' = searchParams.get('tab') === 'resolved' ? 'resolved' : 'active';
  const explicitStatus = searchParams.get('workflow_status');
  const effectiveStatus = explicitStatus
    || (tab === 'resolved' ? RR_RESOLVED_STATES.join(',') : RR_ACTIVE_STATES.join(','));
  const setTab = useCallback((t: 'active' | 'resolved') => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (t === 'active') p.delete('tab'); else p.set('tab', t);
      p.delete('workflow_status'); // the tab partition replaces any strip selection
      p.delete('closure_reason');
      return p;
    });
    setOffset(0);
  }, [setSearchParams]);

  const { data: overview, isLoading: overviewLoading } = useRaOverview();
  const activeCount = RR_ACTIVE_STATES.reduce((n, s) => n + (overview?.workflow_status_counts[s] ?? 0), 0);
  const resolvedCount = RR_RESOLVED_STATES.reduce((n, s) => n + (overview?.workflow_status_counts[s] ?? 0), 0);
  const { data: result, isLoading } = useRiskAcceptances({
    workflow_status: effectiveStatus || undefined,
    closure_reason: searchParams.get('closure_reason') || undefined,
    vendor_id: searchParams.get('vendor_id') || undefined,
    approaching_expiry: searchParams.get('approaching_expiry') === 'true' || undefined,
    has_renewals: searchParams.get('has_renewals') === 'true' ? true : searchParams.get('has_renewals') === 'false' ? false : undefined,
    risk_category: searchParams.get('risk_category') || undefined,
    requires_action_by_user: requiresAction || undefined,
    search: searchTerm || undefined,
    limit: pageSize, offset, sort_by: 'updated_at', sort_order: 'desc',
  });

  const { data: vendorData } = useVendors({ perPage: 100 });
  const vendors = vendorData?.vendors ?? [];
  const vendorName = (vendorId: string): string => vendors.find((v) => v.id === vendorId)?.name ?? vendorId.slice(0, 8) + '…';

  const items = result?.items ?? [];
  const total = result?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const currentPage = Math.floor(offset / pageSize) + 1;
  const { groups, orphans } = groupRaItems(items);

  // Active filter chips (everything except the free-text search; state renders one chip per value).
  const chipEntries: [string, string][] = [];
  for (const s of statusSet) chipEntries.push(['workflow_status', s]);
  if (searchParams.get('closure_reason')) chipEntries.push(['closure_reason', searchParams.get('closure_reason')!]);
  if (searchParams.get('vendor_id')) chipEntries.push(['vendor_id', searchParams.get('vendor_id')!]);
  if (searchParams.get('approaching_expiry') === 'true') chipEntries.push(['approaching_expiry', 'true']);
  if (searchParams.get('has_renewals')) chipEntries.push(['has_renewals', searchParams.get('has_renewals')!]);
  if (searchParams.get('risk_category')) chipEntries.push(['risk_category', searchParams.get('risk_category')!]);
  const anyFilter = chipEntries.length > 0 || requiresAction || !!searchTerm;

  function chipLabel(key: string, value: string): string {
    if (key === 'workflow_status') return STATUS_LABEL[value] ?? value;
    if (key === 'closure_reason') return `Closed · ${value.replace(/_/g, ' ')}`;
    if (key === 'vendor_id') return `Vendor · ${vendorName(value)}`;
    if (key === 'approaching_expiry') return 'Expiring ≤30d';
    if (key === 'has_renewals') return value === 'false' ? 'No renewal' : 'Has renewals';
    if (key === 'risk_category') return `Category · ${value.replace(/_/g, ' ')}`;
    return `${key}: ${value}`;
  }
  function removeChip(key: string, value: string) {
    if (key === 'workflow_status') toggleStatus(value); else setFilter(key, null);
  }

  const KPIS: KpiRender[] = overview ? kpiList(overview, setFilter, toggleStatus, requiresAction) : [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Vendor Risk Register</h1>
          <p className="mt-1 text-sm text-text-secondary">Track vendor risk decisions, upcoming expirations, and items requiring attention.</p>
        </div>
        {canCreate && (
          <Button size="sm" onClick={() => navigate('/risk-acceptances/new')}><Plus className="mr-1.5 h-4 w-4" />New Risk Response</Button>
        )}
      </div>

      {/* Search + composable chips + "Requires my action" toggle */}
      <div className="flex items-center gap-2 w-full md:max-w-[64%]">
        <div className={cn('flex-1 min-w-0 flex items-center gap-1.5 flex-wrap rounded-md border bg-bg-surface px-2.5 py-1.5 transition-colors', anyFilter ? 'border-accent-cyan/60 ring-1 ring-accent-cyan/25' : 'border-border-subtle')}>
          <Search className="h-4 w-4 shrink-0 text-text-secondary" />
          {chipEntries.map(([key, value]) => (
            <button key={`${key}:${value}`} type="button" onClick={() => removeChip(key, value)}
              className="inline-flex items-center gap-1 rounded border border-accent-cyan/40 bg-accent-cyan/10 px-1.5 py-0.5 text-[11px] font-medium text-accent-cyan hover:bg-accent-cyan/20 transition-colors">
              {chipLabel(key, value)}<X className="h-3 w-3" />
            </button>
          ))}
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
            placeholder="Search title, vendor or justification…"
            className="flex-1 min-w-[140px] bg-transparent text-sm text-text-primary placeholder:text-text-secondary focus:outline-none py-0.5" />
          {anyFilter && <button type="button" onClick={clearAll} className="shrink-0 text-[11px] text-text-secondary hover:text-text-primary underline">Clear all</button>}
        </div>
        <Button size="sm" onClick={submitSearch}>Search</Button>
        <Button size="sm" variant={requiresAction ? 'secondary' : 'outline'} onClick={() => setFilter('requires_action_by_user', requiresAction ? null : 'true')}>
          Requires my action
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
        {overviewLoading || !overview
          ? [...Array(6)].map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)
          : KPIS.map((k) => <KpiCard key={k.key} label={k.label} value={k.value} sub={k.sub} info={k.info} onClick={k.onClick} />)}
      </div>

      {/* Lifecycle strip — the 7 native workflow_status by holder (click to filter, multi); the Closed
          holder shows its closure_reason breakdown as display-only sub-counts. */}
      <div className="bg-bg-surface border border-border-subtle rounded-lg p-4">
        <div className="flex items-center gap-1.5 mb-3">
          <span className="text-sm font-semibold text-text-primary">Lifecycle — workflow status</span>
          <InfoTooltip text="Every workflow status a risk response can be in, grouped by who holds it. Click a status to add it to the filter — statuses stack. The Closed group breaks down by closure reason (why it closed) below." />
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-3">
          {HOLDERS.map((h) => (
            <div key={h.label} className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">{h.label}</span>
              <div className="flex flex-wrap gap-1.5">
                {h.states.map((st) => {
                  const count = overview?.workflow_status_counts[st.s] ?? 0;
                  const active = statusSet.has(st.s);
                  return (
                    <button key={st.s} type="button" onClick={() => toggleStatus(st.s)}
                      className={cn('inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors',
                        active ? 'border-accent-cyan bg-accent-cyan/10 text-accent-cyan'
                          : count > 0 ? 'border-border-subtle text-text-primary hover:border-accent-cyan/50'
                            : 'border-border-subtle/50 text-text-faint')}>
                      <span>{st.label}</span>
                      <span className="font-mono tabular-nums font-semibold">{overview ? count : '·'}</span>
                    </button>
                  );
                })}
              </div>
              {/* Closed → closure_reason breakdown (display-only; click a reason to filter closed by reason). */}
              {h.label === 'Closed' && overview && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {CLOSURE_LABEL.filter((c) => (overview.closure_reason_counts[c.r] ?? 0) > 0).map((c) => (
                    <button key={c.r} type="button"
                      onClick={() => { setFilter('workflow_status', 'closed'); setFilter('closure_reason', searchParams.get('closure_reason') === c.r ? null : c.r); }}
                      className={cn('inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition-colors',
                        searchParams.get('closure_reason') === c.r ? 'border-accent-cyan bg-accent-cyan/10 text-accent-cyan' : 'border-border-subtle/60 text-text-secondary hover:border-accent-cyan/40')}>
                      <span>{c.label}</span>
                      <span className="font-mono tabular-nums">{overview.closure_reason_counts[c.r]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* P2 charts — time-to-expiry runway + severity inherent-vs-residual */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(340px,1fr))] gap-3">
        <ChartPanel title="Time to expiry"
          info="Active acceptances bucketed by days until their stored expiry date, plus those already lapsed. The granular version of the “approaching expiry” count. Expiry defaults to one year after the start date when left blank, so treat the dates as approximate; empty buckets are real zeros, not gaps to fill."
          subtitle="approved · by days to expiry">
          {overview ? <ExpiryRunway r={overview.expiry_runway} activeTotal={overview.kpis.active_total} /> : <ChartEmpty note="Loading…" />}
        </ChartPanel>
        <ChartPanel title="Severity — inherent vs residual"
          info="The two stored ratings side by side, exactly as declared — nothing is blended into a score. Residual is only required when compensating controls are recorded, so acceptances without one sit in their own Unspecified column rather than being read as low or dropped from the chart."
          subtitle="approved · two independent series">
          {overview ? <SeverityChart s={overview.severity} activeTotal={overview.kpis.active_total} /> : <ChartEmpty note="Loading…" />}
        </ChartPanel>
      </div>

      {/* P3-A — approval funnel / latency (the granular version of the queue KPIs). */}
      <ChartPanel title="Approval funnel"
        info="How submitted acceptances resolved — approved, returned or denied — with the median days each took, from recorded submission and decision timestamps. Rates are over decided acceptances only. Acceptances still in the approval queue are shown separately below the outcomes, never folded into the approved rate; medians count only acceptances that actually reached that decision."
        subtitle="decided · median days to decision">
        {overview ? <ApprovalFunnel f={overview.funnel} /> : <ChartEmpty note="Loading…" />}
      </ChartPanel>

      {/* P3-B — Requires your action: a VIEW of the same register records (four real conditions). Rows NAVIGATE. */}
      <div className="bg-bg-surface border border-severity-high/40 rounded-lg p-4 flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-semibold text-text-primary inline-flex items-center gap-1.5">Requires your action
            <InfoTooltip text="Acceptances needing action, from four real conditions: coverage lapsed (expired with no successor) · awaiting your approval (submitted by someone else, you are an approver) · expiring within 30 days · returned to you as the submitter. Each row is a real acceptance in the list below; click to open it." />
          </span>
          {overview && <span className={cn('text-[11px] font-mono font-bold rounded-md border px-1.5 py-0.5', overview.attention.length > 0 ? 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border' : 'bg-bg-surface-2 text-text-muted border-border-subtle')}>{overview.attention.length}</span>}
        </div>
        {!overview ? (
          <p className="text-xs text-text-secondary">—</p>
        ) : overview.attention.length === 0 ? (
          <p className="text-sm text-text-secondary">Nothing needs your action right now.</p>
        ) : (
          <>
            {/* ⚠ RR-SCROLL-1: overscroll-contain + min-h-0 keep wheel-scroll INSIDE this nested list (no
                chain to the shell). Mirrors the codebase's correct nested-scroll pattern (VendorBiHeader). */}
            <div className={cn('flex flex-col divide-y divide-border-subtle overflow-y-auto overscroll-contain min-h-0', !attnShowAll && 'max-h-[360px]')}>
              {(attnShowAll ? overview.attention : overview.attention.slice(0, 10)).map((row) => (
                <AttentionRow key={`${row.id}-${row.signal}`} row={row} vendorName={vendorName} onNavigate={(id) => navigate(`/risk-acceptances/${id}`)} />
              ))}
            </div>
            {overview.attention.length > 10 && (
              <button type="button" onClick={() => setAttnShowAll((v) => !v)}
                className="self-start text-xs font-medium text-accent-cyan hover:text-accent-cyan/80 transition-colors">
                {attnShowAll ? 'Show fewer' : `View all ${overview.attention.length}`}
              </button>
            )}
          </>
        )}
      </div>

      {/* Secondary filter — vendor (kept; the strip owns state now) + page-size selector (item 5) */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label htmlFor="vendor-filter" className="text-sm text-text-secondary whitespace-nowrap">Vendor:</label>
          <select id="vendor-filter" value={searchParams.get('vendor_id') ?? ''} onChange={(e) => setFilter('vendor_id', e.target.value || null)}
            className="h-8 rounded-md border border-border-default bg-bg-surface px-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-cyan max-w-[200px]">
            <option value="">All Vendors</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3">
          {/* ⚠ honest cap note — "All" tops out at the backend max (100); the rest is paged. */}
          {pageChoice === 'All' && total > MAX_PAGE_SIZE && (
            <span className="text-[11px] text-text-faint">Showing up to {MAX_PAGE_SIZE} — full list ({total}) is paged</span>
          )}
          <PageSizeSelect value={pageChoice} onChange={(n) => { setPageChoice(n); setOffset(0); }} />
        </div>
      </div>

      {/* ⚠ SS2B-2b Phase 3 — Active/Resolved tabs on ONE register (spec card 8; tab lives in the URL).
          The scoped metrics line states its own scope in words (spec card 9). */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1 border-b border-border-subtle">
          {(['active', 'resolved'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
                tab === t
                  ? 'border-accent-cyan text-text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
              aria-current={tab === t ? 'page' : undefined}
            >
              {t === 'active' ? 'Active' : 'Resolved'}
              <span className="rounded-full bg-bg-surface-2 px-2 py-0.5 text-xs font-bold tabular-nums text-text-secondary">
                {t === 'active' ? activeCount : resolvedCount}
              </span>
            </button>
          ))}
        </div>
        <p className="text-xs text-text-secondary">
          {tab === 'active'
            ? 'Live responses the vendor is still carrying — draft, pending, returned and approved. Implemented mitigations are included; resolved responses are not counted here.'
            : 'Historical responses — closed, denied and withdrawn. These are explicitly excluded from the active counts.'}
        </p>
      </div>

      {/* Table (renewal-chain grouping + empty state) — scoped by the active tab */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-0">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-border-subtle last:border-0">
                  <Skeleton className="h-4 w-32" /><Skeleton className="h-4 flex-1" /><Skeleton className="h-5 w-24 rounded-full" /><Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ShieldAlert className="h-10 w-10 text-text-secondary/40 mb-3" />
              <p className="text-sm font-medium text-text-primary mb-1">
                {anyFilter ? 'No responses match these filters.' : tab === 'resolved' ? 'Nothing resolved yet.' : 'No active risk responses yet.'}
              </p>
              <p className="text-xs text-text-secondary mb-4">
                {anyFilter
                  ? 'Remove a chip above or Clear all to widen the search.'
                  : tab === 'resolved'
                    ? 'Responses appear here once they close, are denied or withdrawn — no sample rows until then.'
                    : 'Create one to get started.'}
              </p>
              {canCreate && !anyFilter && tab === 'active' && (
                <Button size="sm" variant="outline" onClick={() => navigate('/risk-acceptances/new')}><Plus className="mr-1.5 h-3.5 w-3.5" />New Risk Response</Button>
              )}
            </div>
          ) : (
            <>
              {/* ⚠ RR-UX-1 S1: header tracks MUST mirror VendorRenewalChainGroup (7rem date twins, right-anchored). */}
              <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto_7rem_7rem_auto] gap-4 px-4 py-2.5 border-b border-border-subtle bg-bg-surface-2 rounded-t-lg text-xs font-medium text-text-secondary uppercase tracking-wide">
                <div>Vendor</div><div>Title</div><div>State</div><div>Approved</div><div>Expires</div><div className="w-8" />
              </div>
              <div>
                {groups.map(({ parent, children }) => (
                  <VendorRenewalChainGroup key={parent.id} parent={parent} children={children} isApproverClass={isApproverClass}
                    currentUserId={currentUserId} vendorName={vendorName} onNavigate={(id) => navigate(`/risk-acceptances/${id}`)} onAction={(action, entry) => setActionModal({ action, entry })} />
                ))}
                {orphans.map((entry) => (
                  <VendorOrphanChildRow key={entry.id} entry={entry} isApproverClass={isApproverClass}
                    currentUserId={currentUserId} vendorName={vendorName} onNavigate={(id) => navigate(`/risk-acceptances/${id}`)} onAction={(action, entry2) => setActionModal({ action, entry: entry2 })} />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <span>Showing {offset + 1}–{Math.min(offset + pageSize, total)} of {total}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset((p) => Math.max(0, p - pageSize))}><ChevronLeft className="h-4 w-4" />Prev</Button>
            <span className="text-xs">Page {currentPage} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={offset + pageSize >= total} onClick={() => setOffset((p) => p + pageSize)}>Next<ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {actionModal && (
        <RiskAcceptanceActionModal action={actionModal.action} entry={actionModal.entry} onSuccess={() => setActionModal(null)} onClose={() => setActionModal(null)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          mutationHook={useRiskAcceptanceAction as any} />
      )}
    </div>
  );
}

// KPI list builder — copy states the REAL calc; drill-throughs set the honest filter.
function kpiList(o: RaOverview, setFilter: (k: string, v: string | null) => void, toggleStatus: (s: string) => void, requiresAction: boolean): KpiRender[] {
  const k = o.kpis;
  const day = (n: number | null) => (n == null ? '—' : `${n}d`);
  return [
    { key: 'action', label: 'Requires my action', value: String(k.requires_my_action),
      sub: `${k.awaiting_my_approval} awaiting your approval · ${k.returned_to_me} returned to you`,
      info: 'Acceptances where you personally are the next move — waiting on your approval as an approver, or returned to you as the submitter. Uses the action filter the backend already supports.',
      onClick: () => setFilter('requires_action_by_user', requiresAction ? null : 'true') },
    { key: 'pending', label: 'Awaiting approval', value: String(k.awaiting_approval),
      sub: k.awaiting_approval > 0 ? `oldest ${day(k.awaiting_approval_oldest_age_days)} in queue` : undefined,
      info: 'Submitted and sitting in the approval queue, whoever the approver is. The sub-line is the real age of the oldest one from its submitted timestamp — queue depth, not an enterprise count.',
      onClick: () => toggleStatus('pending_approval') },
    { key: 'latency', label: 'Median approval latency', value: k.median_approval_latency_days == null ? '—' : `${k.median_approval_latency_days}d`,
      info: 'Median days from submitted to approved, across acceptances that actually reached approval. Computed from recorded timestamps only — an acceptance still in the queue is not counted as fast; “—” until at least one has completed.' },
    { key: 'expiring', label: 'Expiring in 30 days', value: String(k.expiring_30d),
      sub: `of ${k.active_total} active · ${k.expiring_with_renewal} have a renewal started`,
      info: 'Approved acceptances whose expiry date falls inside the next 30 days — the same window the “approaching expiry” filter uses. Expiry defaults to one year after the start date when left blank, so treat these dates as approximate.',
      onClick: () => setFilter('approaching_expiry', 'true') },
    { key: 'lapsed', label: 'Lapsed without renewal', value: String(k.lapsed_without_renewal),
      sub: `of ${k.expired_total} expired · derived from lineage`,
      info: 'Expired acceptances with no child acceptance pointing back at them. There is no stored “renewed” event — this is derived from parent → child lineage, so read it as “no successor record exists”, not a logged decision not to renew.',
      onClick: () => { setFilter('workflow_status', 'closed'); setFilter('closure_reason', 'expired'); setFilter('has_renewals', 'false'); } },
    { key: 'residual', label: 'Residual rating recorded', value: `${k.residual_recorded}/${k.residual_active_total}`,
      info: 'How many active acceptances carry a residual rating. It is only required when compensating controls are recorded, so a blank is legitimate — a completeness reading, not a compliance score, and the missing ones are never treated as low.' },
  ];
}
